import { useCallback, useState } from 'react';
import { get } from 'lodash';
import DOMPurify from 'dompurify';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import { HookTemplate } from '@rossum/api-client/hookTemplates';
import { DetailDrawer } from '@rossum/ui';
import { Box, Button, Stack, Tooltip } from '@rossum/ui/material';
import { sanitizeLinks } from '../../../../../lib/htmlSanitization';
import { buildAuthLink } from '../../../../../lib/url';
import {
  isUserInDifferentOrganization,
  isUserOrgGroupAdminSelector,
  userSelector,
} from '../../../../../redux/modules/user/selectors';
import { getIcon } from '../../../lib/helpers';
import { extensionDetailPath } from '../../../routes';
import { useCreateHookFromTemplate } from '../hooks/useCreateHookFromTemplate';
import { useIsHookTemplateRestricted } from '../hooks/useIsHookTemplateRestricted';
import { ContactDialog } from './ContactDialog';
import { TokenOwnerDialog } from './TokenOwnerDialog';

DOMPurify.addHook('afterSanitizeAttributes', sanitizeLinks);

export type ExtendedHookTemplate = HookTemplate & {
  tags: string;
};

type Props = {
  extension: ExtendedHookTemplate | null;
  onClose: () => void;
};

export const ExtensionDrawer = ({ onClose, extension }: Props) => {
  const [openDialogType, setOpenDialogType] = useState<
    'contactUs' | 'requestAccess' | 'tokenOwnerSelection' | undefined
  >(undefined);
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const userFromDifferentOrg = useSelector(isUserInDifferentOrganization);
  const isUserOrgGroupAdmin = useSelector(isUserOrgGroupAdminSelector);
  const canBeTokenOwner = !isUserOrgGroupAdmin && !userFromDifferentOrg;

  const { mutate, isLoading: creating } = useCreateHookFromTemplate();
  const isRestricted = useIsHookTemplateRestricted(extension);

  const intl = useIntl();

  const handleCreateHookFromTemplate = useCallback(
    (hookTemplate: ExtendedHookTemplate, tokenOwner: string | null) => {
      mutate(
        {
          payload: {
            events: hookTemplate.events,
            hookTemplate: hookTemplate.url,
            name: hookTemplate.name,
            queues: [],
            tokenOwner,
          },
        },
        {
          onSuccess: hook => {
            dispatch(
              push({
                pathname: `${extensionDetailPath(hook.id)}`,
              })
            );
          },
        }
      );
    },
    [dispatch, mutate]
  );

  const handleHookTemplateButtonClick = useCallback(
    (hookTemplate: ExtendedHookTemplate) => {
      if (hookTemplate.useTokenOwner && !canBeTokenOwner) {
        setOpenDialogType('tokenOwnerSelection');
        return;
      }

      handleCreateHookFromTemplate(
        hookTemplate,
        hookTemplate.useTokenOwner ? user.url : null
      );
    },
    [canBeTokenOwner, handleCreateHookFromTemplate, user.url]
  );

  return (
    <DetailDrawer
      titleIcon={
        extension
          ? getIcon(extension.type, get(extension.config, 'runtime'))
          : null
      }
      title={extension?.name}
      open={extension !== null}
      keepMounted={false}
      onClose={onClose}
    >
      {extension && (
        <Stack
          padding={3}
          height="100%"
          spacing={2}
          justifyContent="space-between"
          data-cy="extension-drawer-content"
        >
          <Box
            component="div"
            sx={{
              overflow: 'auto',
              [`p, ul, ol`]: {
                mb: 2,
              },
              [`li`]: {
                mb: 1,
              },
              [`a`]: {
                color: t => t.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
            }}
            // oxlint-disable-next-line react/no-danger -- oxlint stricter than eslint — sanitized HTML, pre-existing usage
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(extension.storeDescription),
            }}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            <Button
              variant="outlined"
              color="secondary"
              data-cy="extensions-contact-us"
              onClick={() => setOpenDialogType('contactUs')}
            >
              {intl.formatMessage({
                id: 'containers.settings.extensions.store.contactUs',
              })}
            </Button>
            {isRestricted || extension.installAction === 'request_access' ? (
              <Button
                variant="contained"
                color="primary"
                data-cy="extensions-request-access"
                onClick={() => setOpenDialogType('requestAccess')}
              >
                {intl.formatMessage({
                  id: 'containers.settings.extensions.store.requestAccess',
                })}
              </Button>
            ) : extension.externalUrl ? (
              <Tooltip
                title={intl.formatMessage({
                  id: 'containers.settings.extensions.store.useExtensionTooltip.external',
                })}
              >
                <Button
                  variant="contained"
                  data-cy="extensions-try-extension"
                  href={buildAuthLink(extension.externalUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  sx={{ '&:hover': { color: 'primary.contrastText' } }}
                >
                  {intl.formatMessage({
                    id: 'containers.settings.extensions.store.useExtension',
                  })}
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                data-cy="extensions-try-extension"
                onClick={() => handleHookTemplateButtonClick(extension)}
              >
                {intl.formatMessage({
                  id: 'containers.settings.extensions.store.useExtension',
                })}
              </Button>
            )}
          </Stack>
          {openDialogType !== 'tokenOwnerSelection' && (
            <ContactDialog
              type={openDialogType}
              open={
                openDialogType === 'requestAccess' ||
                openDialogType === 'contactUs'
              }
              onClose={() => setOpenDialogType(undefined)}
              extensionName={extension.name}
              dataCy="extension-drawer-contact-dialog"
            />
          )}

          <TokenOwnerDialog
            open={openDialogType === 'tokenOwnerSelection'}
            onSubmit={(tokenOwner: string) =>
              handleCreateHookFromTemplate(extension, tokenOwner)
            }
            submitting={creating}
            onClose={() => setOpenDialogType(undefined)}
            dataCy="extension-drawer-token-owner-dialog"
          />
        </Stack>
      )}
    </DetailDrawer>
  );
};
